import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const slug = (text) =>
  String(text)
    .toLowerCase()
    .replace(/[`*_]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const textOf = (children) =>
  React.Children.toArray(children)
    .map((c) => (typeof c === "string" ? c : c?.props?.children ? textOf(c.props.children) : ""))
    .join("");

/**
 * Renders a documentation markdown file (GitHub flavoured) in the dashboard theme,
 * with anchored headings and an "On this page" list built from the `##` headings.
 * `query` keeps only the `##` sections whose text contains it (case-insensitive).
 */
const DocsMarkdown = ({ markdown, toc = true, query = "" }) => {
  const q = String(query || "").trim().toLowerCase();
  const visible = useMemo(() => {
    if (!markdown || !q) return markdown;
    const parts = markdown.split(/\r?\n(?=## )/);
    const kept = parts.filter((p, i) => (i === 0 && !/^## /.test(p) ? false : p.toLowerCase().includes(q)));
    return kept.join("\n");
  }, [markdown, q]);
  const sections = useMemo(() => {
    if (!toc || !visible) return [];
    return visible
      .split(/\r?\n/)
      .filter((l) => /^## /.test(l))
      .map((l) => l.replace(/^## /, "").trim())
      .map((title) => ({ title: title.replace(/[`*]/g, ""), id: slug(title) }));
  }, [visible, toc]);

  if (!markdown) return <p className="apDocsLoading">Loading…</p>;
  if (q && !visible) {
    return (
      <div className="apDocs">
        <p className="apDocsLoading">No section mentions “{query}”. Try another word (an endpoint, an error code, a field name).</p>
      </div>
    );
  }

  const heading = (Tag) => ({ children }) => {
    const id = slug(textOf(children));
    return (
      <Tag id={id}>
        <a href={`#${id}`} className="apDocsAnchor">{children}</a>
      </Tag>
    );
  };

  return (
    <div className="apDocs">
      {sections.length > 1 && (
        <nav className="apDocsToc" aria-label="On this page">
          <div className="apDocsTocTitle">{q ? `${sections.length} matching section${sections.length === 1 ? "" : "s"}` : "On this page"}</div>
          <ul>
            {sections.map((s) => (
              <li key={s.id}><a href={`#${s.id}`}>{s.title}</a></li>
            ))}
          </ul>
        </nav>
      )}
      <article className="apDocsBody">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: heading("h1"),
            h2: heading("h2"),
            h3: heading("h3"),
            a: ({ href, children }) => {
              const external = /^https?:\/\//.test(href || "");
              return (
                <a href={href} {...(external ? { target: "_blank", rel: "noreferrer" } : {})}>{children}</a>
              );
            },
            table: ({ children }) => <div className="apDocsTable"><table>{children}</table></div>,
          }}
        >
          {visible}
        </ReactMarkdown>
      </article>
    </div>
  );
};

export default DocsMarkdown;
